/* eslint-disable react/no-multi-comp */
// not sure if loading here is better than calling from react
import React, { Dispatch, SetStateAction, useEffect, useState, useRef } from 'react'; 
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  LinearProgress,
  LinearProgressProps,
  Typography,
  Grid
} from '@material-ui/core';

import { AssessmentPayloadProps} from '../../../common/entityUtilities';

import {useUserState} from '../../../context/UserContext';
import { useUploadDispatch } from '../../../context/UploadContext';
import {
  createAssessmentAndDeliverFeedback
} from '../../../common/utilities';


import { useTranslation } from 'react-i18next';
import {
  AssessmentCreationFeedBackUIProps
} from '../../../common/entityUtilities';

import { PromptResult } from '@capacitor/dialog';
import { showPrompt, showAlert, checkQuota } from '../../../common/utilities';
import { SaveStatusTypes } from '../../../common/entityUtilities';
import { all } from 'axios';
import { debugLog } from '../../../common/debugUtilities';

// this elemnt takes the data from the assessment creating dialog and
// goes through the creation process with 
function LinearProgressWithLabel(props:LinearProgressProps & { value: number }) {
  return (
    <Box
      alignItems="center"
      display="flex"
    >
      <Box
        mr={1}
        width="100%"
      >
        <LinearProgress
          variant="determinate"
          {...props}
        />
      </Box>
      <Box minWidth={35}>
        <Typography
          color="textSecondary"
          variant="body2"
        >
          {`${Math.round(props.value)}%`}
        </Typography>
      </Box>
    </Box>
  );
}

const useStyles = makeStyles({
  root: {
    width: '100%',
  },
});

export default function AsmtCreationProgressComponent(props:{
  asmtSaveValues:AssessmentPayloadProps|undefined, 
  setSavingStatus:Dispatch<SetStateAction<SaveStatusTypes>>}):JSX.Element {
  // closeParent:()=>void}

  const user = useUserState();
  const setUploadState = useUploadDispatch();
  const {t, i18n} = useTranslation();
  const {asmtSaveValues, setSavingStatus} =  props;//, closeParent
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [progressInfo, setProgressInfo] = useState('Initializing');
  const saveRef = useRef(asmtSaveValues);
  // Guard against the upload-driver effect firing more than once for a single
  // upload. Without this, an upstream re-render or React StrictMode double-fire
  // can re-enter passThroughCreateAssessment, leading to duplicate uploads and
  // a duplicate "video received" alert.
  const uploadStartedRef = useRef(false);
  // Will need a tri state saving/in progress/complete
  // const [creatingAsmtStatus, setIsCreatingAsmt] = useState<SaveStatusTypes>(SaveStatusTypes.INITIALIZE);
  const [savingProcessStatus, setSavingProcessStatus] = useState<AssessmentCreationFeedBackUIProps>();
  let timerToCancel:any;

  async function fakeUpdateOverTime():Promise<AssessmentCreationFeedBackUIProps> {
    return await new Promise(resolve => {
      timerToCancel = setInterval(() => {
        setSavingProcessStatus((prevSavingStatus)=>{       
          // We want increments of 10%
          let totalSize =  prevSavingStatus?.totalSize;
          let increment = 10;
          if(totalSize){
            increment = totalSize/10;
          }else{
            totalSize = 100;
            increment = 10;
          }
          let bytesUp = prevSavingStatus?.bytesTransferred;
          if(bytesUp){
            bytesUp += increment;
          }else{
            bytesUp = increment;
          }
          const newValue:AssessmentCreationFeedBackUIProps = {
            ...(prevSavingStatus as AssessmentCreationFeedBackUIProps),
            bytesTransferred:bytesUp
          };
          if(bytesUp > totalSize){
            newValue.bytesTransferred = 0;
            resolve(newValue);
            clearInterval(timerToCancel);
          }
          return newValue;
        });
      }, 500);
    });
  }
  
  const displayFakeUploadTime= async (asmtCreationPayload:AssessmentPayloadProps|undefined,
    setStatus:Dispatch<React.SetStateAction<AssessmentCreationFeedBackUIProps|undefined>>)
    :Promise<void>=>{
    if(!asmtCreationPayload) return;
    let count = 0;
    const videoCount = asmtCreationPayload.videosForUpload.length;
    for(const video of asmtCreationPayload.videosForUpload){
      count+=1;
      setStatus((prevSavingStatus)=>{
        const newValue:AssessmentCreationFeedBackUIProps = {
          ...(prevSavingStatus as AssessmentCreationFeedBackUIProps),
          videoTotal:videoCount, currentVideoCount:count,
          fileName:video.name, totalSize:video.size
        };
        return newValue;
      });
    }

    await fakeUpdateOverTime();
    debugLog('Done with fake upload');
  };

  // This is where the video file gets uploaded
  React.useEffect(() => {

    async function passThroughCreateAssessment():Promise<void>{

        // Only run if we actually have a payload AND we haven't already kicked
        // off an upload for this mount. Without this guard, a parent re-render
        // (e.g. from sibling state updates that follow inline subject creation)
        // can cause the effect to re-run, leading to duplicate uploads and a
        // duplicate "video received" alert.
        if (!asmtSaveValues) return;
        if (uploadStartedRef.current) {
            debugLog('[useEffect] Upload already started for this payload; skipping re-entry.');
            return;
        }
        uploadStartedRef.current = true;

        const allowed = await checkQuota(user.firebaseUser, asmtSaveValues.subjectId);
        if (!allowed) {
            await showAlert(t('quotatitle',{ns:'videoupload'}),
            t('quotamsg',{ns:'videoupload'}),
            t('ok',{ns:'videoupload'}));
            setSavingStatus(SaveStatusTypes.DONE);
            return;
        }
        debugLog('[useEffect] Saving asmt:', asmtSaveValues);
        await createAssessmentAndDeliverFeedback(user.firebaseUser,
            asmtSaveValues, setSavingProcessStatus, t, user.authLevel, setUploadState);

        const message = t('videoreceived',{ns:'uploadvideo'}); // video received message
        await showAlert(t('submitted',{ns:'uploadvideo'}), message, t('ok',{ns:'videoupload'}));

        setSavingStatus(SaveStatusTypes.DONE);
    }

    passThroughCreateAssessment();
    return () => {
        debugLog('Clearing timer on component unmount');
        clearInterval(timerToCancel);
        // Note: We do NOT reset upload state here because upload continues
        // in background even if dialog is closed. Upload state is reset by
        // the Firebase upload completion callback in utilities.ts
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asmtSaveValues]);

  useEffect(()=>{
    if(!savingProcessStatus) return;
    const {fileName, videoTotal, totalSize, genericMessage,
      bytesTransferred, currentVideoCount, uploadSpeed, eta} = savingProcessStatus;

    // Format file size for display
    const formatBytes = (bytes: number) => {
      if (bytes === 0) return '0 MB';
      const mb = bytes / (1024 * 1024);
      return mb.toFixed(2) + ' MB';
    };

    // Format time for display
    const formatTime = (seconds: number) => {
      if (!seconds || seconds <= 0) return '';
      if (seconds < 60) return `${Math.round(seconds)}s`;
      const minutes = Math.floor(seconds / 60);
      const secs = Math.round(seconds % 60);
      return `${minutes}m ${secs}s`;
    };

    // Build progress text with speed and ETA
    let text = `Uploading ${fileName}, video ${currentVideoCount} of ${videoTotal}`;

    if (uploadSpeed && uploadSpeed > 0) {
      text += ` - ${uploadSpeed.toFixed(2)} MB/s`;
    }

    if (eta && eta > 0) {
      text += ` - ETA: ${formatTime(eta)}`;
    }

    if (bytesTransferred && totalSize) {
      text += ` (${formatBytes(bytesTransferred)} / ${formatBytes(totalSize)})`;
    }

    if(genericMessage){
      setProgressInfo(genericMessage);
    }else{
      setProgressInfo(text);
    }

    if(bytesTransferred && totalSize){
      const perc = Math.floor(100*(bytesTransferred/totalSize));
      setProgressPercentage(perc);
    }
    // Note: Upload state is now updated directly in Firebase callback (utilities.ts)
    // This ensures indicator persists even when component unmounts
  },[savingProcessStatus]);

  return (
    <Grid container>
      <Grid
        item
        xs={12}
      >
        <Typography>
          {progressInfo}
        </Typography>
      </Grid>
      <Grid
        item
        xs={12}
      >
        <LinearProgressWithLabel value={progressPercentage} />
      </Grid>
    </Grid>
  );
}